import type { ICaveatCounters } from '@skystone/shared';

interface CaveatBannerSlotProps {
  /** Counters from the latest successful FABRIC_SONARQUBE_SYNC run. Null when
   *  no successful run exists yet (fresh deployment) — banner falls back to a
   *  status-unavailable copy. */
  counters: ICaveatCounters | null;
}

/**
 * Caveat banner — fixed at the top of the Code Quality tab. Uses the same
 * visual treatment as `QualityCaveatsBanner` (rounded muted card, info icon,
 * two-paragraph layout) but with dynamic copy driven by live sync counters.
 *
 * The numbers come from `code-quality-summary` response → `caveatCounters`,
 * which the service reads from `fabric_sync_runs.counters` JSONB.
 */
export function CaveatBannerSlot({ counters }: Readonly<CaveatBannerSlotProps>) {
  return (
    <div
      role="note"
      aria-label="Code Quality coverage caveats"
      className="flex items-start gap-3 rounded-md border border-border bg-muted/30 px-4 py-3"
      data-testid="code-quality-caveat-banner"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-muted-foreground text-[11px] font-semibold text-muted-foreground"
      >
        i
      </span>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          Code Quality coverage is partial — only repos tracked in SonarCloud show here
        </p>
        <CaveatBody counters={counters} />
      </div>
    </div>
  );
}

function CaveatBody({ counters }: Readonly<{ counters: ICaveatCounters | null }>) {
  if (counters === null) {
    return (
      <p className="text-xs text-muted-foreground">
        Sonar coverage status unavailable — no successful sync run yet. Counts will appear here
        after the first <code className="rounded bg-muted px-1 py-0.5">FABRIC_SONARQUBE_SYNC</code>{' '}
        completes.
      </p>
    );
  }
  const total = counters.matched + counters.unmatched + counters.ambiguous;
  return (
    <p className="text-xs text-muted-foreground">
      <span className="font-semibold text-foreground">{counters.matched}</span> of{' '}
      <span className="font-semibold text-foreground">{total}</span> Sonar projects matched to a
      repo. <span className="font-semibold text-foreground">{counters.unmatched}</span> unmatched,{' '}
      <span className="font-semibold text-foreground">{counters.ambiguous}</span> ambiguous —
      onboarding rolls out team-by-team. Until that's complete, "by Team" aggregates only the repos
      we currently measure.
    </p>
  );
}
